import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from '../../environments/environment';
import { MessageListTypeEnum } from '../../views/messaging/enums/message-list-type.enum';
import { EntityTypeEnum } from '../enums/entity-type.enum';
import { SubUrlsEnum } from '../enums/sub-urls.enum';
import {
    IMessage,
    IMessageContact,
    IMessageDTO,
    IMessageFilters,
    IMessages,
    IMessagesDTO,
    IMessagingContact,
    IUnreadMessagesDTO,
} from '../interfaces/messaging.interface';
import { ISearchContactDTO, ISearchContactsDTO } from '../interfaces/users/user.interface';
import { MappersService } from './mappers.service';

@Injectable({
    providedIn: 'root',
})
export class MessagingService {
    constructor(private http: HttpClient, private mapperService: MappersService) {}

    public getReceivedMessages(
        page: number,
        msgPerPage: number,
        studentId?: number,
        filters?: IMessageFilters
    ): Observable<IMessages> {
        return this.http
            .get<IMessagesDTO>(environment.wasoApi + SubUrlsEnum.MSG_RECEIVED, {
                params: this.buildMessageParams(page, msgPerPage, { studentId, filters }),
            })
            .pipe(
                map((result: IMessagesDTO) => ({
                    total_items: result.total_items,
                    total_pages: result.total_pages,
                    messages: this.mapperService.fromIMessageDTOArraytoIMessage(result.messages),
                }))
            );
    }

    public getArchivedMessages(
        page: number,
        msgPerPage: number,
        studentId?: number,
        filters?: IMessageFilters
    ): Observable<IMessages> {
        return this.http
            .get<IMessagesDTO>(environment.wasoApi + SubUrlsEnum.MSG_DELETED, {
                params: this.buildMessageParams(page, msgPerPage, { studentId, filters }),
            })
            .pipe(
                map((result: IMessagesDTO) => ({
                    total_items: result.total_items,
                    total_pages: result.total_pages,
                    messages: this.mapperService.fromIMessageDTOArraytoIMessage(result.messages),
                }))
            );
    }

    public getSendedMessages(
        page: number,
        msgPerPage: number,
        studentId?: number,
        filters?: IMessageFilters
    ): Observable<IMessages> {
        return this.http
            .get<IMessagesDTO>(environment.wasoApi + SubUrlsEnum.MSG_SENDED, {
                params: this.buildMessageParams(page, msgPerPage, { studentId, filters }),
            })
            .pipe(
                map((result: IMessagesDTO) => ({
                    total_items: result.total_items,
                    total_pages: result.total_pages,
                    messages: this.mapperService.fromIMessageDTOArraytoIMessage(result.messages),
                }))
            );
    }

    public searchMessages(
        search: string,
        messageType: MessageListTypeEnum,
        page: number,
        msgPerPage: number,
        studentId?: number,
        filters?: IMessageFilters
    ): Observable<IMessages> {
        const subUrl: string =
            messageType === MessageListTypeEnum.SENT
                ? SubUrlsEnum.MSG_SENDED
                : messageType === MessageListTypeEnum.RECEIVED
                ? SubUrlsEnum.MSG_RECEIVED
                : SubUrlsEnum.MSG_DELETED;
        return this.http
            .get<IMessagesDTO>(environment.wasoApi + subUrl, {
                params: this.buildMessageParams(page, msgPerPage, { search, studentId, filters }),
            })
            .pipe(
                map((result: IMessagesDTO) => ({
                    total_items: result.total_items,
                    total_pages: result.total_pages,
                    messages: this.mapperService.fromIMessageDTOArraytoIMessage(result.messages),
                }))
            );
    }

    public sendMessage(message: IMessage): Observable<any> {
        const httpOptions: any = {
            headers: new HttpHeaders({
                ['Content-Type']: 'multipart/form-data',
            }),
        };

        const formData: FormData = new FormData();

        message.recipients.forEach((c: IMessagingContact) => {
            if (
                [
                    EntityTypeEnum.EMPLOYEE,
                    EntityTypeEnum.PARENT,
                    EntityTypeEnum.STUDENT,
                    EntityTypeEnum.ADMINISTRATOR,
                    EntityTypeEnum.RELATIVE,
                ].includes(c.entityType)
            ) {
                formData.append('recipient_ids[]', c.id.split('-')[1]);
            }
            if (c.entityType === EntityTypeEnum.CLASS) {
                formData.append('class_ids[]', c.id.split('-')[1]);
            }
            if (c.entityType === EntityTypeEnum.GROUP) {
                formData.append('group_ids[]', c.id.split('-')[1]);
            }
            if (!c.entityType) {
                formData.append('recipient_ids[]', c.id);
            }
        });
        message.inCopy.forEach((c: IMessagingContact) => {
            if (
                [
                    EntityTypeEnum.EMPLOYEE,
                    EntityTypeEnum.PARENT,
                    EntityTypeEnum.STUDENT,
                    EntityTypeEnum.ADMINISTRATOR,
                    EntityTypeEnum.RELATIVE,
                ].includes(c.entityType)
            ) {
                formData.append('copy_recipient_ids[]', c.id.split('-')[1]);
            }
            if (c.entityType === EntityTypeEnum.CLASS) {
                formData.append('copy_class_ids[]', c.id.split('-')[1]);
            }
            if (c.entityType === EntityTypeEnum.GROUP) {
                formData.append('copy_group_ids[]', c.id.split('-')[1]);
            }
            if (!c.entityType) {
                formData.append('copy_recipient_ids[]', c.id);
            }
        });
        formData.append('message[subject]', message.subject);
        formData.append('message[message]', message.message);
        formData.append('message[is_important]', message.important ? 'true' : 'false');

        if (message.attachedFile?.length > 0) {
            message.attachedFile.forEach((f: any) => {
                formData.append('message[files][]', f);
            });
        }
        return this.http
            .post<any>(environment.wasoApi + SubUrlsEnum.MESSAGES, formData, httpOptions)
            .pipe(map((result: any) => this.mapperService.fromIMessageDTOtoIMessage(result)));
    }

    public markAsViewed(messageId: number): Observable<IMessage> {
        const headers: HttpHeaders = new HttpHeaders();
        return this.http
            .put<any>(environment.wasoApi + SubUrlsEnum.MESSAGES + '/' + messageId, { message: { is_read: true } }, { headers })
            .pipe(map((result: any) => this.mapperService.fromIMessageDTOtoIMessage(result)));
    }

    public archiveMessage(messageId: number): Observable<IMessage> {
        const headers: HttpHeaders = new HttpHeaders();
        return this.http
            .delete<any>(environment.wasoApi + SubUrlsEnum.MESSAGES + '/' + messageId, { headers })
            .pipe(map((result: any) => this.mapperService.fromIMessageDTOtoIMessage(result)));
    }

    public unArchiveMessage(messageId: number): Observable<IMessage> {
        const headers: HttpHeaders = new HttpHeaders();
        return this.http
            .post<any>(environment.wasoApi + SubUrlsEnum.UNARCHIVE + messageId + '/restore', { headers })
            .pipe(map((result: any) => this.mapperService.fromIMessageDTOtoIMessage(result)));
    }

    public getContacts(filters: { search?: string; educationLevelIds?: number[] }): Observable<IMessageContact[]> {
        let params: HttpParams = new HttpParams();
        if (filters?.search) {
            params = params.set('search', filters.search);
        }
        if (filters?.educationLevelIds?.length) {
            params = params.append('education_level_ids', filters.educationLevelIds.join(','));
        }
        return this.http
            .get<ISearchContactsDTO>(environment.wasoApi + SubUrlsEnum.SEARCH_CONTACTS, {
                params: filters?.educationLevelIds?.length || filters?.search ? params : null,
            })
            .pipe(map((result: ISearchContactsDTO) => this.searchContactDTOtoContact(result)));
    }

    public getUnreadMessagesCount(): Observable<IUnreadMessagesDTO> {
        return this.http.get<IUnreadMessagesDTO>(environment.wasoApi + SubUrlsEnum.UNREAD_MESSAGES_COUNT);
    }

    public getMessageById(id: number): Observable<IMessage> {
        return this.http
            .get<IMessageDTO>(environment.wasoApi + SubUrlsEnum.MESSAGES + `/${id}`)
            .pipe(map((result: IMessageDTO) => this.mapperService.fromIMessageDTOtoIMessage(result)));
    }

    private buildMessageParams(
        page: number,
        msgPerPage: number,
        options: { search?: string; studentId?: number; filters?: IMessageFilters } = {}
    ): HttpParams {
        let params: HttpParams = new HttpParams().set('page', page).set('per_page', msgPerPage);

        if (options.studentId) {
            params = params.set('viewing_student_id', options.studentId);
        }
        if (options.search) {
            params = params.set('search', options.search);
        }
        if (options.filters) {
            if (options.filters.important !== undefined) {
                params = params.set('important', options.filters.important);
            }
            if (options.filters.informative !== undefined) {
                params = params.set('informative', options.filters.informative);
            }
            if (options.filters.educationLevelIds?.length) {
                params = params.set('education_level_ids', options.filters.educationLevelIds.join(','));
            }
        }

        return params;
    }

    private searchContactDTOtoContact(result: ISearchContactsDTO): IMessageContact[] {
        const list: IMessageContact[] = [];
        result.school_classes.forEach((r: ISearchContactDTO) => {
            list.push({
                id: r.id.toString(),
                entity_type: EntityTypeEnum.CLASS,
                display_name: r.name,
            });
        });
        result.groups.forEach((r: ISearchContactDTO) => {
            list.push({
                id: r.id.toString(),
                entity_type: EntityTypeEnum.GROUP,
                display_name: r.name,
            });
        });
        result.administrators.forEach((r: ISearchContactDTO) => {
            list.push({
                id: r.id.toString(),
                entity_type: EntityTypeEnum.ADMINISTRATOR,
                display_name: r.name,
            });
        });
        result.employees.forEach((r: ISearchContactDTO) => {
            list.push({
                id: r.id.toString(),
                entity_type: EntityTypeEnum.EMPLOYEE,
                display_name: r.name,
            });
        });
        result.parents.forEach((r: ISearchContactDTO) => {
            list.push({
                id: r.id.toString(),
                entity_type: EntityTypeEnum.PARENT,
                display_name: r.name,
            });
        });
        return list;
    }
}
